<script setup lang="ts">
import { renderMarkdown } from "@/lib/markdown";
import { computed, ref, watch } from "vue";

import { ApiError, apiClient } from "@/api/client";
import type { MockQuestion, MockSubmitResponse } from "@/api/types";
import RlCard from "@/components/ui/RlCard.vue";
import RlPill from "@/components/ui/RlPill.vue";
import { formatClock } from "@/lib/focusSession";

/**
 * Mock exam paper (PRD §6.8 PE-8.7, design handoff §5 screen 8).
 *
 * A mock runs **bare, under exam conditions**: no focus bar, no break offer, no
 * inactivity nudge, and no focus block (contract D1 restricts
 * `focus_blocks.mode` to practice|tutor). Nothing in here may grow a focus
 * affordance — the whole point of the mode is that the session wrapper is off.
 *
 * The amber countdown is exam conditions, which is legitimate time pressure for
 * a mock. It is **not** a loss-aversion device: plain `mm:ss` and a draining
 * bar, no colour escalation, no "hurry up" copy, no warnings at thresholds.
 *
 * The container owns the tick; this component only renders `secondsLeft`. It
 * does own the *submission*, though, because the typed answers live here — see
 * the zero watcher below.
 */

const props = defineProps<{
  sessionId: string;
  questions: MockQuestion[];
  secondsLeft: number;
  /** 0 for an untimed mock, which renders no timer bar at all. */
  totalSeconds: number;
}>();

const emit = defineEmits<{ submitted: [MockSubmitResponse]; close: [] }>();

/**
 * Answers keyed by interaction id rather than by index so jumping around the
 * question map cannot mis-file an answer, and so a failed submit can put every
 * one of them back on screen untouched.
 */
const answers = ref<Record<string, string>>({});
const currentIdx = ref(0);
const submitting = ref(false);
const error = ref<string | null>(null);

const current = computed<MockQuestion | undefined>(
  () => props.questions[currentIdx.value],
);
const renderedQuestion = computed(() =>
  current.value ? renderMarkdown(current.value.question_text) : "",
);

const timed = computed(() => props.totalSeconds > 0);
const clock = computed(() => formatClock(props.secondsLeft));
/** Bar drains left-to-right as time is spent. */
const remainingPct = computed(() =>
  timed.value
    ? Math.min(100, Math.max(0, (props.secondsLeft / props.totalSeconds) * 100))
    : 100,
);

function isAnswered(q: MockQuestion): boolean {
  return (answers.value[q.interaction_id] ?? "").trim().length > 0;
}

const answeredCount = computed(() => props.questions.filter(isAnswered).length);

function goTo(idx: number): void {
  if (idx >= 0 && idx < props.questions.length) currentIdx.value = idx;
}

async function submit(): Promise<void> {
  if (submitting.value) return;
  submitting.value = true;
  error.value = null;

  const payload = props.questions.map((q) => ({
    interaction_id: q.interaction_id,
    answer_text: (answers.value[q.interaction_id] ?? "").trim(),
  }));

  try {
    emit("submitted", await apiClient.submitMock(props.sessionId, payload));
  } catch (e) {
    // `answers` is never cleared here: marking a whole paper is a long LLM call
    // and the most likely failure is a timeout, so losing the student's typed
    // answers on the way back would be the worst possible outcome.
    error.value =
      e instanceof ApiError && e.status === 429
        ? "The marker is busy right now. Your answers are still here — try again in a moment."
        : "Could not submit for marking. Your answers are still here — try again.";
    submitting.value = false;
  }
  // On success the container swaps us for MockResults, so `submitting` stays
  // true and the paper cannot be double-submitted during the unmount.
}

/**
 * The container owns the clock but not the answers, so the auto-submit has to
 * fire from here. It runs once: `submitting` latches, and a failed auto-submit
 * hands control back to the student with everything they wrote intact.
 */
watch(
  () => props.secondsLeft,
  (left) => {
    if (timed.value && left <= 0 && !submitting.value) void submit();
  },
);
</script>

<template>
  <div class="mock">
    <RlCard flush>
      <!-- No clock at all on an untimed mock, so nothing implies a limit the
           student did not choose. -->
      <div
        v-if="timed"
        class="timer"
        role="timer"
        :aria-label="`${clock} remaining`"
      >
        <div
          class="timer-fill"
          :style="{ width: `${remainingPct}%` }"
          aria-hidden="true"
        />
        <span class="timer-clock">{{ clock }}</span>
      </div>
      <div class="bar">
        <RlPill tone="warn">Mock · exam conditions</RlPill>
        <span class="counter"
          >Question {{ currentIdx + 1 }} of {{ questions.length }}</span
        >
        <!-- Leaving mid-marking would throw the whole paper away, so the exit
             is closed for the few seconds the request is in flight. -->
        <button
          type="button"
          class="secondary"
          :disabled="submitting"
          @click="$emit('close')"
        >
          Leave
        </button>
        <button
          type="button"
          class="primary"
          :disabled="submitting"
          @click="submit"
        >
          Submit for marking
        </button>
      </div>
    </RlCard>

    <div v-if="submitting" class="marking">
      <div class="spinner rl-spin" aria-hidden="true" />
      <p class="marking-copy">Marking your paper against the mark scheme…</p>
      <p class="marking-sub">
        {{ questions.length }} questions. This takes a little while.
      </p>
    </div>

    <div v-else class="grid">
      <RlCard>
        <div v-if="current" class="q-head">
          <span class="q-label">Question {{ currentIdx + 1 }}</span>
          <span class="q-marks">{{ current.marks }} marks</span>
        </div>
        <p v-if="current" class="q-topic">
          {{ current.topic }} · {{ current.question_type.replace("_", " ") }}
        </p>
        <!-- Question text is markdown from our own generator, rendered the
             same way practice renders it. -->
        <div class="q-text" v-html="renderedQuestion" />

        <textarea
          v-if="current"
          v-model="answers[current.interaction_id]"
          class="answer"
          rows="9"
          :aria-label="`Answer to question ${currentIdx + 1}`"
          placeholder="Write your answer…"
        />

        <div class="nav">
          <button
            type="button"
            class="secondary"
            :disabled="currentIdx === 0"
            @click="goTo(currentIdx - 1)"
          >
            Previous
          </button>
          <button
            type="button"
            class="secondary"
            :disabled="currentIdx >= questions.length - 1"
            @click="goTo(currentIdx + 1)"
          >
            Next
          </button>
        </div>

        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </RlCard>

      <RlCard>
        <div class="map-label">Question map</div>
        <div class="map">
          <button
            v-for="(q, i) in questions"
            :key="q.interaction_id"
            type="button"
            class="map-cell"
            :class="{ answered: isAnswered(q), current: i === currentIdx }"
            :aria-current="i === currentIdx ? 'true' : undefined"
            :aria-label="`Question ${i + 1}, ${isAnswered(q) ? 'answered' : 'not answered'}`"
            @click="goTo(i)"
          >
            {{ i + 1 }}
          </button>
        </div>
        <p class="map-count">
          {{ answeredCount }} of {{ questions.length }} answered
        </p>
      </RlCard>
    </div>
  </div>
</template>

<style scoped>
.mock {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  width: 100%;
  max-width: 860px;
}

/* ── Countdown ───────────────────────────────────────────── */
.timer {
  position: relative;
  height: 30px;
  background: var(--rl-bg-muted);
}

.timer-fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--rl-warn-bg);
  border-right: 2px solid var(--rl-warn-fg);
  transition: width 1s linear;
}

.timer-clock {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  font-weight: 600;
  color: var(--rl-fg-1);
}

@media (prefers-reduced-motion: reduce) {
  .timer-fill {
    transition: none;
  }
}

.bar {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-5);
}

.counter {
  margin-left: auto;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

/* ── Layout ──────────────────────────────────────────────── */
.grid {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: var(--sp-4);
  align-items: start;
}

@media (max-width: 720px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

/* ── Question ────────────────────────────────────────────── */
.q-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.q-label,
.q-marks {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

.q-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.q-topic {
  margin: var(--sp-1) 0 var(--sp-3);
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.q-text {
  font-size: var(--rl-text-lg);
  line-height: 1.45;
  color: var(--rl-fg-1);
}

.q-text :deep(p) {
  margin: 0 0 var(--sp-2);
}

.answer {
  width: 100%;
  min-height: 150px;
  margin-top: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  line-height: 1.55;
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border);
  border-radius: var(--radius);
  resize: vertical;
}

.nav {
  display: flex;
  justify-content: space-between;
  margin-top: var(--sp-3);
}

.error {
  margin: var(--sp-3) 0 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-danger-fg);
}

/* ── Question map ────────────────────────────────────────── */
.map-label {
  margin-bottom: var(--sp-3);
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rl-fg-3);
}

.map {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--sp-2);
}

.map-cell {
  aspect-ratio: 1;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  font-weight: 600;
  color: var(--rl-fg-2);
  background: var(--rl-bg);
  border: 1.5px solid var(--rl-border-subtle);
  border-radius: var(--radius);
  cursor: pointer;
}

/* Answered and unanswered must differ by fill as well as by weight: colour
   alone would not survive a colour-vision deficiency, and knowing what is left
   is the entire job of the map. */
.map-cell.answered {
  color: var(--rl-green-700);
  background: var(--rl-green-100);
  border-color: var(--rl-green-200);
}

.map-cell.current {
  border-color: var(--rl-green-600);
  box-shadow: var(--shadow-focus);
}

.map-count {
  margin: var(--sp-3) 0 0;
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

/* ── Marking wait ────────────────────────────────────────── */
.marking {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-10) 0;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--rl-border-subtle);
  border-top-color: var(--rl-fg-3);
  border-radius: 50%;
}

.marking-copy {
  margin: 0;
  font-size: var(--rl-text-md);
  color: var(--rl-fg-1);
}

.marking-sub {
  margin: 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}
</style>
